import { AdminRole, AdminPermissions, ROLE_PERMISSIONS } from '@/types';
import { supabase } from '@/lib/supabase';

let cachedPermissions: AdminPermissions | null = null;

export async function hasPermission(role: AdminRole | null, permission: keyof AdminPermissions): Promise<boolean> {
  if (!role) return false;

  try {
    // Use cached permissions if available
    if (cachedPermissions) {
      return cachedPermissions[permission] ?? false;
    }

    // Get user permissions from database
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data: userPermissions } = await supabase
      .from('user_permissions')
      .select('permissions')
      .eq('user_id', user.id)
      .single();

    if (userPermissions) {
      cachedPermissions = userPermissions.permissions;
      return userPermissions.permissions[permission] ?? false;
    }

    // Fallback to role-based permissions if no user_permissions found
    return ROLE_PERMISSIONS[role]?.[permission] ?? false;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
}

export async function canAccessRoute(role: AdminRole | null, route: string): Promise<boolean> {
  if (!role) return false;

  switch (route) {
    case '/admin/customers':
      return await hasPermission(role, 'manageUsers');
    case '/admin/statistics':
      return await hasPermission(role, 'viewAnalytics');
    case '/admin/edge-api-endpoint':
      return role === 'founder';
    case '/admin/bot-config':
      return role === 'founder';
    case '/admin/claude-stats':
      return role === 'founder';
    case '/admin/health':
      return await hasPermission(role, 'viewHealth');
    case '/admin/permissions':
      return await hasPermission(role, 'editPermissions');
    case '/admin/email-templates':
      return await hasPermission(role, 'manageSystem');
    default:
      if (route.includes('/admin/customers/')) {
        if (route.includes('/subscription')) {
          return await hasPermission(role, 'manageSubscriptions');
        }
        if (route.includes('/sso')) {
          return await hasPermission(role, 'manageSSO');
        }
      }
      return false;
  }
}

export function canViewCompanyStats(userRole: string, companyId: string | null, targetCompanyId: string): boolean {
  if (userRole === 'admin') return true;
  return userRole === 'employer' && companyId === targetCompanyId;
}

export function canViewClaudeStats(adminRole: AdminRole | null): boolean {
  return adminRole === 'founder';
}

export function canViewAnalytics(adminRole: AdminRole | null): boolean {
  if (!adminRole) return false;
  return ROLE_PERMISSIONS[adminRole].viewAnalytics;
}

export function clearPermissionsCache(): void {
  cachedPermissions = null;
}

export async function refreshPermissions(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    cachedPermissions = null;
    return;
  }

  const { data: userPermissions } = await supabase
    .from('user_permissions')
    .select('permissions')
    .eq('user_id', user.id)
    .single();

  if (userPermissions) {
    cachedPermissions = userPermissions.permissions;
  } else {
    cachedPermissions = null;
  }
}
